import type { IdealDesignDto, IdealItemDto, IdealPartDto, IdealSiteDto } from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { DutyFacts } from './Duty';
import { Card } from '../../../ui';
import { ItemBase } from './Labels';
import { LibrarySources } from './LibrarySources';

/**
 * Progetto ideale di un passo (ADR 0024): l'impresa, poi ogni sede e, dentro, ogni attività che vi si svolge. È un
 * riferimento ragionato dal sistema, non un dato dell'azienda: le voci sono cose da avere o da fare, raggruppate.
 * «Organizzazione e ruoli» ha il suo organigramma (OrganizationStep).
 */
export function IdealDesign({ d }: { d: IdealDesignDto }) {
  // Mentre si progetta il passo mostra il suo loader: qui arriva solo il progetto finito.
  return (
    <div className="space-y-3">
      {d.impresa && (d.impresa.voci.length > 0 || d.impresa.generating || d.impresa.error) && (
        <Level title="L’impresa" subtitle="vale per tutte le sedi" state={d.impresa}>
          <Part p={d.impresa} />
        </Level>
      )}
      {d.sedi.map((s) => (
        <SiteDesign key={s.sede_id} s={s} open={d.sedi.length <= 2} />
      ))}
      {!d.sedi.length && (
        <p className="text-sm text-ink/50">
          Nessuna sede ancora: quando ci sono, progetto ogni sede e ogni attività che vi si svolge.
        </p>
      )}
    </div>
  );
}

function Level({
  title,
  subtitle,
  state,
  children,
}: {
  title: string;
  subtitle?: string;
  state: { generating: boolean; error: string | null };
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <p className="mb-2 text-sm font-semibold">
        {title}
        {subtitle && <span className="font-normal text-ink/50"> · {subtitle}</span>}
      </p>
      {state.error && !state.generating && (
        <p className="mb-2 text-sm text-red-700">Il progetto non è riuscito: {ownerError(state.error)}</p>
      )}
      {children}
    </Card>
  );
}

function SiteDesign({ s, open }: { s: IdealSiteDto; open: boolean }) {
  const info = [s.luogo, s.addetti != null ? `${s.addetti} addetti` : 'addetti non noti']
    .filter(Boolean)
    .join(' · ');
  return (
    <Card className="p-0">
      <details open={open} className="group">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3">
          <span className="text-ink/40 transition group-open:rotate-90">▸</span>
          <span className="min-w-0 flex-1">
            <span className="text-sm font-semibold">📍 {s.sede}</span>
            <span className="block text-xs text-ink/50">
              {info}
              {s.attivita.length > 0 && ` · ${s.attivita.map((a) => a.attivita).join(', ')}`}
            </span>
          </span>
        </summary>
        <div className="space-y-4 border-t border-black/5 px-4 py-3">
          {s.error && !s.generating && (
            <p className="text-sm text-red-700">Il progetto della sede non è riuscito: {ownerError(s.error)}</p>
          )}
          {s.sede_in_se && (s.sede_in_se.voci.length > 0 || s.sede_in_se.note.length > 0) && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-accent/80">
                La sede
              </p>
              <Part p={s.sede_in_se} />
            </div>
          )}
          {s.attivita.map((a) => (
            <div key={a.attivita} className="rounded-xl border border-black/5 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-accent/80">
                Attività · {a.attivita}
              </p>
              {a.come_si_svolge && <p className="mb-2 mt-1 text-sm">{a.come_si_svolge}</p>}
              <Part p={a} />
            </div>
          ))}
          {s.senza_attivita && (
            <p className="text-sm text-amber-700">
              Per questa sede non risultano attività economiche: indicale nel passo «Attività
              economiche» e progetto come dovrebbero svolgersi qui.
            </p>
          )}
          {!s.generating &&
            !s.error &&
            !s.sede_in_se &&
            !s.attivita.length &&
            !s.senza_attivita && (
              <p className="text-sm text-ink/50">Ancora nulla di progettato per questa sede.</p>
            )}
        </div>
      </details>
    </Card>
  );
}

/** Le voci di un livello, per gruppi, con le ipotesi del progetto. */
function Part({ p }: { p: IdealPartDto }) {
  if (!p.voci.length && !p.note.length) return null;
  return (
    <div>
      <Groups voci={p.voci} />
      {p.note.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
          {p.note.map((n) => (
            <li key={n}>△ {n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Detail({ v }: { v: IdealItemDto }) {
  return (
    <div className="mt-1 space-y-1 text-xs text-ink/70">
      {v.descrizione && <p>{v.descrizione}</p>}
      {v.dettagli.length > 0 && (
        <ul className="list-disc pl-4">
          {v.dettagli.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      <DutyFacts v={v} />
      {v.perche && <p className="text-ink/45">Perché: {v.perche}</p>}
      {v.riferimento && (
        <p className="text-ink/45">
          <ItemBase v={v} /> {v.riferimento}
        </p>
      )}
      <LibrarySources refs={v.libreria} fondata={v.fondata} conti={v.conti} />
    </div>
  );
}

function Groups({ voci }: { voci: IdealItemDto[] }) {
  const groups = new Map<string, IdealItemDto[]>();
  for (const v of voci) groups.set(v.gruppo ?? '', [...(groups.get(v.gruppo ?? '') ?? []), v]);
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {[...groups].map(([g, list]) => (
        <div key={g} className="rounded-lg bg-black/[0.025] p-2.5">
          {g && <p className="mb-1 text-xs font-semibold text-ink/70">{g}</p>}
          <ul className="space-y-1.5">
            {list.map((v) => (
              <li key={v.nome} className="text-sm">
                <details>
                  <summary className="cursor-pointer list-none">
                    <span className="font-medium">{v.nome}</span>
                    {v.quantita && (
                      <span className="ml-1.5 rounded bg-accent/10 px-1.5 text-xs text-accent">
                        {v.quantita}
                      </span>
                    )}
                    {v.riporta_a && (
                      <span className="ml-1.5 text-xs text-ink/45">· fa capo a {v.riporta_a}</span>
                    )}
                    {v.descrizione && (
                      <span className="block text-xs text-ink/55">{v.descrizione}</span>
                    )}
                  </summary>
                  <Detail v={{ ...v, descrizione: '' }} />
                </details>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
